import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaSync, FaEye, FaEyeSlash } from 'react-icons/fa';
import { QUIZMASTER_API } from '../config';
import '../styles/styles.css';
import FloatingChat from './FloatingChat';

const FlashcardPage = ({ accessToken, supabaseRefreshToken, onSignIn }) => {
    const [flashcards, setFlashcards] = useState([]);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFlipped, setIsFlipped] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const selectedFlashcardId = location.state?.selectedFlashcardId;
    const document = location.state?.selectedDocument;
    const flashcardsFromList = location.state?.flashcards || [];

    const handleReturnToFlashcards = useCallback(() => {
        if (document) {
            navigate('/flashcard-list', { state: { selectedDocument: document } });
        } else {
            navigate('/profile');
        }
    }, [document, navigate]);

    const refreshToken = useCallback(async () => {
        try {
            const refreshResponse = await fetch(`${QUIZMASTER_API}/auth/refresh`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: supabaseRefreshToken }),
            });
            if (!refreshResponse.ok) throw new Error('Token refresh failed');
            const { access_token: newAccessToken, refresh_token: newRefreshToken } = await refreshResponse.json();
            onSignIn({ accessToken: newAccessToken, refreshToken: newRefreshToken });
            return newAccessToken;
        } catch (err) {
            throw new Error('Session expired. Please sign in again.');
        }
    }, [supabaseRefreshToken, onSignIn]);

    const fetchWithAuth = useCallback(async (url, options = {}) => {
        let response = await fetch(url, {
            ...options,
            headers: { ...options.headers, 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' },
        });
        if (response.status === 401 && supabaseRefreshToken) {
            const newAccessToken = await refreshToken();
            response = await fetch(url, {
                ...options,
                headers: { ...options.headers, 'Authorization': `Bearer ${newAccessToken}`, 'Accept': 'application/json' },
            });
        }
        return response;
    }, [accessToken, supabaseRefreshToken, refreshToken]);

    useEffect(() => {
        if (flashcardsFromList.length > 0) {
            // Use flashcards passed from the list
            setFlashcards(flashcardsFromList);
            const initialIndex = flashcardsFromList.findIndex(card => card.id === selectedFlashcardId);
            setCurrentCardIndex(initialIndex >= 0 ? initialIndex : 0);
            setLoading(false);
        } else if (document) {
            // Fetch flashcards if not provided
            const fetchFlashcards = async () => {
                try {
                    const response = await fetchWithAuth(
                        `${QUIZMASTER_API}/get-flashcards-by-document/?document_id=${document.id}`
                    );
                    if (!response.ok) throw new Error('Failed to fetch flashcards');
                    const data = await response.json();
                    const fetchedFlashcards = data.flashcards || [];
                    setFlashcards(fetchedFlashcards);
                    const initialIndex = fetchedFlashcards.findIndex(card => card.id === selectedFlashcardId);
                    setCurrentCardIndex(initialIndex >= 0 ? initialIndex : 0);
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchFlashcards();
        } else {
            setError('No document or flashcards selected.');
            setLoading(false);
        }
    }, [document, selectedFlashcardId, flashcardsFromList, fetchWithAuth]);

    const handlePreviousCard = () => {
        setCurrentCardIndex((prev) => (prev === 0 ? flashcards.length - 1 : prev - 1));
        setIsFlipped(false);
        setShowAnswer(false);
    };

    const handleNextCard = () => {
        setCurrentCardIndex((prev) => (prev === flashcards.length - 1 ? 0 : prev + 1));
        setIsFlipped(false);
        setShowAnswer(false);
    };

    const toggleCard = () => {
        setIsFlipped(!isFlipped);
        if (!isFlipped) {
            setShowAnswer(true);
        }
    };

    const handleKeyPress = useCallback((event) => {
        if (event.key === 'ArrowLeft') {
            handlePreviousCard();
        } else if (event.key === 'ArrowRight') {
            handleNextCard();
        } else if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            toggleCard();
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [handleKeyPress]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading flashcards...</p>
            </div>
        );
    }

    if (error || flashcards.length === 0) {
        return (
            <div className="error-container">
                <p>{error || 'No flashcards found.'}</p>
                <button className="button confirm-button" onClick={handleReturnToFlashcards}>
                    Back to Flashcards
                </button>
            </div>
        );
    }

    const currentCard = flashcards[currentCardIndex];
    const progressPercentage = ((currentCardIndex + 1) / flashcards.length) * 100;

    return (
        <div className="scroll-container flashcard-page">
            <div className="quiz-page__header">
                <div className="quiz-page__titles">
                    <h1 className="quiz-page__title">{document?.filename ? `${document.filename} Flashcards` : 'Flashcard Review'}</h1>
                    <p className="quiz-page__subtitle">
                        Study at your own pace. Use arrow keys or click to navigate. Press spacebar to flip cards.
                    </p>
                </div>
                <div className="quiz-page__meta-group">
                    <div className="quiz-status-pill" aria-live="polite">
                        <span className="quiz-status-pill__label">Card</span>
                        <span className="quiz-status-pill__value">{currentCardIndex + 1} / {flashcards.length}</span>
                    </div>
                    <div className="quiz-status-pill" aria-live="polite">
                        <span className="quiz-status-pill__label">Difficulty</span>
                        <span className="quiz-status-pill__value">{currentCard.difficulty}</span>
                    </div>
                </div>
            </div>

            <div className="quiz-progress" aria-hidden="true">
                <div className="quiz-progress__bar">
                    <div className="quiz-progress__fill" style={{ width: `${progressPercentage}%` }}></div>
                </div>
            </div>

            <article className="flashcard-container" aria-labelledby="flashcard-heading">
                <div className={`flashcard ${isFlipped ? 'flashcard--flipped' : ''}`} onClick={toggleCard}>
                    <div className="flashcard__inner">
                        <div className="flashcard__front">
                            <header className="flashcard__header">
                                <p className="flashcard__eyebrow">Question</p>
                                <h2 id="flashcard-heading" className="flashcard__title">{currentCard.front_text}</h2>
                            </header>
                            <footer className="flashcard__footer">
                                <span className="flashcard__helper">Click to reveal answer</span>
                            </footer>
                        </div>
                        <div className="flashcard__back">
                            <header className="flashcard__header">
                                <p className="flashcard__eyebrow">Answer</p>
                                <h2 className="flashcard__title">{currentCard.back_text}</h2>
                            </header>
                            <footer className="flashcard__footer">
                                <span className="flashcard__helper">Click to see question</span>
                            </footer>
                        </div>
                    </div>
                </div>

            </article>

            <div className="quiz-page__actions">
                <button
                    type="button"
                    className="button button--ghost quiz-page__back"
                    onClick={handleReturnToFlashcards}
                >
                    Back to Flashcards
                </button>
                <div className="quiz-page__nav">
                    <button
                        type="button"
                        className="button button--secondary"
                        onClick={handlePreviousCard}
                    >
                        <FaArrowLeft />
                        Previous
                    </button>
                    <button
                        type="button"
                        className="button"
                        onClick={handleNextCard}
                    >
                        Next
                        <FaArrowRight />
                    </button>
                </div>
            </div>
            {document && (
                <FloatingChat
                    documentId={document.id}
                    filename={document.filename}
                    accessToken={accessToken}
                />
            )}
        </div>
    );
};

export default FlashcardPage;

